import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Avatar,
  Paper,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Article as ArticleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  LinkedIn as LinkedInIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  TrendingUp as TrendingUpIcon,
  TouchApp as TouchAppIcon,
  RemoveRedEye as EyeIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSettingsAccounts } from '../../store/settingsSlice';
import { fetchLinkedInAnalytics, fetchLinkedInShareStatistics } from '../../Action/socialMediaAPI';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LinkedInAnalytics = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const accounts = useSelector((state) => state.settings.accounts || []);
  const accountsLoading = useSelector((state) => state.settings.status === 'loading');
  
  console.log('All accounts loaded:', accounts);
  
  // Filter LinkedIn accounts
  const linkedInAccounts = useMemo(() => {
    const filtered = accounts.filter(acc => acc?.provider?.toLowerCase() === 'linkedin');
    console.log('Filtered LinkedIn accounts:', filtered);
    return filtered;
  }, [accounts]);

  // Component state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [shareStatistics, setShareStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load accounts on mount
  useEffect(() => {
    console.log('Component mounted, fetching accounts...');
    dispatch(fetchSettingsAccounts());
  }, [dispatch]);

  // Auto-select first LinkedIn account
  useEffect(() => {
    if (linkedInAccounts.length > 0 && !selectedAccount) {
      console.log('Auto-selecting first LinkedIn account:', linkedInAccounts[0]);
      setSelectedAccount(linkedInAccounts[0]);
    }
  }, [linkedInAccounts, selectedAccount]);

  // Fetch analytics function
  const fetchAnalytics = useCallback(async () => {
    if (!selectedAccount) return;

    console.log('Fetching LinkedIn analytics for account:', selectedAccount);

    // Get credentials from selected account
    const pageId = selectedAccount.username || selectedAccount.accountName;
    const accessToken = selectedAccount.accessToken;

    console.log('Using credentials:', { pageId, accessToken: accessToken ? '***' : 'missing' });

    if (!pageId || !accessToken) {
      setError('LinkedIn account configuration is incomplete. Please reconnect your LinkedIn account in Settings to continue.');
      return;
    }

    // Clear old data before fetching new data
    setAnalyticsData(null);
    setShareStatistics(null);
    setLoading(true);
    setError(null);

    // Fetch both posts and share statistics
    const [postsResult, shareStatsResult] = await Promise.all([
      fetchLinkedInAnalytics(pageId, accessToken),
      fetchLinkedInShareStatistics(pageId, accessToken)
    ]);
    
    console.log('LinkedIn posts API result:', postsResult);
    console.log('LinkedIn share statistics result:', shareStatsResult);

    setLoading(false);

    if (postsResult.success) {
      setAnalyticsData(postsResult.data);
    } else {
      let friendlyMessage = postsResult.message || 'Failed to load LinkedIn analytics';
      
      // Check for specific HTTP status codes and provide friendly messages
      if (postsResult.message?.includes('HTTP error! status: 500')) {
        friendlyMessage = 'LinkedIn server is temporarily unavailable. Please try again in a few moments.';
      } else if (postsResult.message?.includes('401')) {
        friendlyMessage = 'Your LinkedIn access token has expired. Please reconnect your LinkedIn account in Settings.';
      } else if (postsResult.message?.includes('403')) {
        friendlyMessage = 'Access denied. Please check your LinkedIn account permissions in Settings.';
      } else if (postsResult.message?.includes('429')) {
        friendlyMessage = 'LinkedIn API rate limit reached. Please wait a few minutes before trying again.';
      } else if (postsResult.message?.includes('Network error') || postsResult.message?.includes('fetch')) {
        friendlyMessage = 'Unable to connect to LinkedIn. Please check your internet connection.';
      }
      
      setError(friendlyMessage);
    }

    // Set share statistics if available
    if (shareStatsResult.success) {
      setShareStatistics(shareStatsResult.data);
      if (!postsResult.success) {
        // If posts failed but stats succeeded, clear the error
        setError(null);
      }
    } else if (!postsResult.success) {
      // Only set error if both failed
      setAnalyticsData(null);
      setShareStatistics(null);
    }
  }, [selectedAccount]);

  // Fetch analytics when account is selected
  useEffect(() => {
    if (selectedAccount) {
      console.log('Selected account changed, fetching analytics...');
      fetchAnalytics();
    }
  }, [selectedAccount, fetchAnalytics]);

  const handleAccountChange = (event) => {
    const accountId = event.target.value;
    const account = linkedInAccounts.find(acc => acc.accountId === accountId);
    console.log('Account changed to:', account);
    setSelectedAccount(account);
  };

  const handleRefresh = () => {
    console.log('Refresh button clicked');
    fetchAnalytics();
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) return null;

    const responseData = analyticsData[0];
    const posts = responseData?.elements || [];
    const paging = responseData?.paging || {};

    const publishedPosts = posts.filter(post => post.lifecycleState === 'PUBLISHED');
    
    // Count media types
    const imagePosts = posts.filter(post => post.content?.media?.id?.includes(':image:')).length;
    const videoPosts = posts.filter(post => post.content?.media?.id?.includes(':video:')).length;
    const textPosts = posts.length - imagePosts - videoPosts;

    return {
      totalPosts: paging.total || posts.length,
      publishedPosts: publishedPosts.length,
      displayedPosts: posts.length,
      imagePosts,
      videoPosts,
      textPosts,
      pageStart: paging.start || 0,
      pageCount: paging.count || posts.length,
    };
  }, [analyticsData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) return null;

    const posts = analyticsData[0]?.elements || [];
    
    // Take last 10 posts for trend (they come in reverse chronological order)
    const recentPosts = posts.slice(0, 10).reverse();

    // Extract data for charts
    const labels = recentPosts.map(post => {
      const date = new Date(post.createdAt);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const engagementTrend = recentPosts.map(post => {
      // LinkedIn doesn't provide engagement in this API, so we'll show post count
      return 1;
    });

    // Media type distribution
    const imagePosts = posts.filter(post => post.content?.media?.id?.includes(':image:')).length;
    const videoPosts = posts.filter(post => post.content?.media?.id?.includes(':video:')).length;
    const textPosts = posts.length - imagePosts - videoPosts;

    // Post timing distribution (by hour of day)
    const postsByHour = Array(24).fill(0);
    posts.forEach(post => {
      const hour = new Date(post.createdAt).getHours();
      postsByHour[hour]++;
    });

    // Weekly posting pattern
    const postsByDay = Array(7).fill(0);
    posts.forEach(post => {
      const day = new Date(post.createdAt).getDay();
      postsByDay[day]++;
    });

    return {
      // Line chart: Post frequency trend
      postFrequency: {
        labels,
        datasets: [{
          label: 'Posts Published',
          data: engagementTrend,
          borderColor: '#0A66C2',
          backgroundColor: 'rgba(10, 102, 194, 0.1)',
          fill: true,
          tension: 0.4,
        }],
      },

      // Bar chart: Posts by day of week
      postsByDay: {
        labels: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        datasets: [{
          label: 'Number of Posts',
          data: postsByDay,
          backgroundColor: 'rgba(10, 102, 194, 0.8)',
          borderColor: '#0A66C2',
          borderWidth: 1,
        }],
      },

      // Doughnut chart: Content type distribution
      contentTypes: {
        labels: ['Image Posts', 'Video Posts', 'Text Posts'],
        datasets: [{
          data: [imagePosts, videoPosts, textPosts],
          backgroundColor: [
            'rgba(10, 102, 194, 0.8)',
            'rgba(136, 108, 192, 0.8)',
            'rgba(56, 178, 172, 0.8)',
          ],
          borderColor: [
            '#0A66C2',
            '#886CC0',
            '#38B2AC',
          ],
          borderWidth: 2,
        }],
      },

      // Horizontal bar: Peak posting times (top 5 hours)
      peakTimes: {
        labels: postsByHour
          .map((count, hour) => ({ hour, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)
          .map(item => `${item.hour}:00 - ${item.hour}:59`),
        datasets: [{
          label: 'Posts',
          data: postsByHour
            .map((count, hour) => ({ hour, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
            .map(item => item.count),
          backgroundColor: 'rgba(10, 102, 194, 0.8)',
          borderColor: '#0A66C2',
          borderWidth: 1,
        }],
      },
    };
  }, [analyticsData]);

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 12, weight: 'bold' },
          color: '#333',
        },
      },
      title: {
        display: true,
        text: 'Recent Posts Timeline',
        font: { size: 16, weight: 'bold' },
        color: '#0A66C2',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#555',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          color: '#555',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 12, weight: 'bold' },
          color: '#333',
        },
      },
      title: {
        display: true,
        text: 'Posts by Day of Week',
        font: { size: 16, weight: 'bold' },
        color: '#0A66C2',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#555',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          color: '#555',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { size: 12, weight: 'bold' },
          color: '#333',
          padding: 15,
        },
      },
      title: {
        display: true,
        text: 'Content Type Distribution',
        font: { size: 16, weight: 'bold' },
        color: '#0A66C2',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        cornerRadius: 8,
      },
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 12, weight: 'bold' },
          color: '#333',
        },
      },
      title: {
        display: true,
        text: 'Top 5 Peak Posting Times',
        font: { size: 16, weight: 'bold' },
        color: '#0A66C2',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#555',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      y: {
        ticks: {
          color: '#555',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Loading state
  if (accountsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#0A66C2' }} />
      </Box>
    );
  }

  // No accounts state
  if (linkedInAccounts.length === 0) {
    return (
      <Card sx={{ mt: 2, p: 3, textAlign: 'center', borderRadius: 3, boxShadow: 3 }}>
        <LinkedInIcon sx={{ fontSize: 60, color: '#0A66C2', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold">
          No LinkedIn Accounts Connected
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please connect your LinkedIn account in Settings to view analytics.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ 
            background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
            color: 'white',
            '&:hover': { background: 'linear-gradient(135deg, #004182 0%, #0A66C2 100%)' }
          }}
        >
          Go to Settings
        </Button>
      </Card>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#0A66C2', width: 56, height: 56 }}>
                <LinkedInIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#0A66C2' }}>
                  LinkedIn Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your professional network performance
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl 
              fullWidth 
              variant="outlined" 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(10, 102, 194, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(10, 102, 194, 0.15)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0A66C2',
                    },
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 16px rgba(10, 102, 194, 0.2)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0A66C2',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#0A66C2',
                  },
                },
              }}
            >
              <InputLabel>Select Account</InputLabel>
              <Select
                value={selectedAccount?.accountId || ''}
                onChange={handleAccountChange}
                label="Select Account"
              >
                {linkedInAccounts.map((account) => (
                  <MenuItem 
                    key={account.accountId} 
                    value={account.accountId}
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(10, 102, 194, 0.08)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(10, 102, 194, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(10, 102, 194, 0.16)',
                        },
                      },
                    }}
                  >
                    <LinkedInIcon sx={{ fontSize: 20, color: '#0A66C2' }} />
                    <span>{account.accountName || account.username}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }} />}
              onClick={handleRefresh}
              disabled={loading || !selectedAccount}
              fullWidth
              size="medium"
              sx={{
                background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
                color: 'white',
                fontWeight: 'bold',
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(10, 102, 194, 0.3)',
                textTransform: 'none',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #004182 0%, #0A66C2 100%)',
                  boxShadow: '0 6px 20px rgba(10, 102, 194, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(10, 102, 194, 0.3)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                  color: '#9e9e9e',
                  boxShadow: 'none',
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Error State */}
      {error && (
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: 3,
          border: '2px solid #ef4444',
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#ef4444', width: 56, height: 56 }}>
                  <CloseIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="#991b1b">
                    Unable to Load Analytics
                  </Typography>
                  <Typography variant="body2" color="#7f1d1d">
                    We encountered an issue while fetching your LinkedIn data
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ borderColor: '#fca5a5' }} />
              
              <Box>
                <Typography variant="body2" fontWeight="bold" color="#991b1b" sx={{ mb: 1 }}>
                  Error Details:
                </Typography>
                <Typography variant="body2" color="#7f1d1d">
                  {error}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{
                    bgcolor: '#ef4444',
                    color: 'white',
                    '&:hover': { bgcolor: '#dc2626' },
                  }}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setError(null)}
                  sx={{
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    '&:hover': { borderColor: '#dc2626', color: '#dc2626' },
                  }}
                >
                  Dismiss
                </Button>
              </Box>
              
              <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight="bold" color="#991b1b" sx={{ mb: 1 }}>
                  💡 Troubleshooting Tips:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
                  <li>Check if your LinkedIn access token has expired</li>
                  <li>Verify your account permissions are properly configured</li>
                  <li>Ensure you have a stable internet connection</li>
                  <li>Try switching to a different LinkedIn account in the dropdown</li>
                </Typography>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress sx={{ color: '#0A66C2' }} size={60} />
        </Box>
      )}

      {/* Share Statistics - Engagement Metrics */}
      {!loading && !error && shareStatistics && shareStatistics.elements && shareStatistics.elements.length > 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#0A66C2', mb: 1 }}>
              📊 Engagement Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total engagement metrics across all your LinkedIn posts
            </Typography>
          </Box>
          
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {/* Impressions */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 12px rgba(10, 102, 194, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(10, 102, 194, 0.25)',
                    transform: 'translateY(-4px)',
                  },
                  background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                  border: '1px solid #0A66C2',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: '#0A66C2', 
                      width: 56, 
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 8px rgba(10, 102, 194, 0.3)',
                    }}>
                      <EyeIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                      Total Impressions
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#0A66C2' }}>
                      {shareStatistics.elements[0].totalShareStatistics.impressionCount.toLocaleString()}
                    </Typography>
                    <Chip 
                      label="Views" 
                      size="small" 
                      sx={{ 
                        mt: 1.5,
                        bgcolor: '#0A66C2',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Unique Impressions */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 12px rgba(131, 58, 180, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(131, 58, 180, 0.25)',
                    transform: 'translateY(-4px)',
                  },
                  background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
                  border: '1px solid #833AB4',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: '#833AB4', 
                      width: 56, 
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 8px rgba(131, 58, 180, 0.3)',
                    }}>
                      <VisibilityIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                      Unique Impressions
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#833AB4' }}>
                      {shareStatistics.elements[0].totalShareStatistics.uniqueImpressionsCount.toLocaleString()}
                    </Typography>
                    <Chip 
                      label="Reach" 
                      size="small" 
                      sx={{ 
                        mt: 1.5,
                        bgcolor: '#833AB4',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Clicks */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(255, 152, 0, 0.25)',
                    transform: 'translateY(-4px)',
                  },
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                  border: '1px solid #FF9800',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: '#FF9800', 
                      width: 56, 
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)',
                    }}>
                      <TouchAppIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                      Total Clicks
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#FF9800' }}>
                      {shareStatistics.elements[0].totalShareStatistics.clickCount.toLocaleString()}
                    </Typography>
                    <Chip 
                      label="Interactions" 
                      size="small" 
                      sx={{ 
                        mt: 1.5,
                        bgcolor: '#FF9800',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Likes */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.25)',
                    transform: 'translateY(-4px)',
                  },
                  background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                  border: '1px solid #4CAF50',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: '#4CAF50', 
                      width: 56, 
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                    }}>
                      <ThumbUpIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                      Total Likes
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#4CAF50' }}>
                      {shareStatistics.elements[0].totalShareStatistics.likeCount.toLocaleString()}
                    </Typography>
                    <Chip 
                      label="Reactions" 
                      size="small" 
                      sx={{ 
                        mt: 1.5,
                        bgcolor: '#4CAF50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Comments */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.25)',
                    transform: 'translateY(-4px)',
                  },
                  background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                  border: '1px solid #2196F3',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: '#2196F3', 
                      width: 56, 
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)',
                    }}>
                      <CommentIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                      Total Comments
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#2196F3' }}>
                      {shareStatistics.elements[0].totalShareStatistics.commentCount.toLocaleString()}
                    </Typography>
                    <Chip 
                      label="Discussions" 
                      size="small" 
                      sx={{ 
                        mt: 1.5,
                        bgcolor: '#2196F3',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Engagement Rate - Full Width Highlight Card */}
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 8px 24px rgba(10, 102, 194, 0.2)',
                  background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  },
                }}
              >
                <CardContent>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Avatar sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.2)', 
                          width: 80, 
                          height: 80,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        }}>
                          <TrendingUpIcon sx={{ fontSize: 48 }} />
                        </Avatar>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Typography variant="h6" fontWeight="600" sx={{ opacity: 0.95, mb: 0.5 }}>
                        Overall Engagement Rate
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Percentage of people who engaged with your content after seeing it
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h2" fontWeight="bold" sx={{ 
                          fontSize: { xs: '2.5rem', md: '3.5rem' },
                          textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}>
                          {(shareStatistics.elements[0].totalShareStatistics.engagement * 100).toFixed(2)}%
                        </Typography>
                        <Chip 
                          label="Engagement" 
                          sx={{ 
                            mt: 1,
                            bgcolor: 'rgba(255,255,255,0.25)',
                            color: 'white',
                            fontWeight: 'bold',
                            backdropFilter: 'blur(10px)',
                          }} 
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Shares (if available) */}
            {shareStatistics.elements[0].totalShareStatistics.shareCount !== undefined && (
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  sx={{ 
                    borderRadius: 3, 
                    boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(156, 39, 176, 0.25)',
                      transform: 'translateY(-4px)',
                    },
                    background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
                    border: '1px solid #9C27B0',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                          Total Shares
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#9C27B0' }}>
                          {shareStatistics.elements[0].totalShareStatistics.shareCount.toLocaleString()}
                        </Typography>
                      </Box>
                      <Avatar sx={{ 
                        bgcolor: '#9C27B0', 
                        width: 56, 
                        height: 56,
                        boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)',
                      }}>
                        <ShareIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Analytics Content */}
      {!loading && !error && analyticsData && stats && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: '500' }}>
                        Total Posts
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                        {stats.totalPosts}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 50, height: 50 }}>
                      <ArticleIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #886CC0 0%, #5F4B8B 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: '500' }}>
                        Published
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                        {stats.publishedPosts}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 50, height: 50 }}>
                      <VisibilityIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #38B2AC 0%, #2C7A7B 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: '500' }}>
                        Media Posts
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                        {stats.imagePosts + stats.videoPosts}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 50, height: 50 }}>
                      <ImageIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: '500' }}>
                        Video Posts
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                        {stats.videoPosts}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 50, height: 50 }}>
                      <VideoIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Post Frequency Trend */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ height: 350 }}>
                    <Line data={chartData.postFrequency} options={lineChartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Posts by Day of Week */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ height: 350 }}>
                    <Bar data={chartData.postsByDay} options={barChartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Content Type Distribution */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ height: 350 }}>
                    <Doughnut data={chartData.contentTypes} options={doughnutOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Peak Posting Times */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ height: 350 }}>
                    <Bar data={chartData.peakTimes} options={horizontalBarOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Posts List */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#0A66C2' }}>
                Recent Posts
              </Typography>
              <Grid container spacing={2}>
                {analyticsData[0]?.elements?.slice(0, 9).map((post, index) => (
                  <Grid item xs={12} sm={6} md={4} key={post.id}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        boxShadow: 2,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Chip 
                          label={post.lifecycleState} 
                          size="small"
                          sx={{ 
                            bgcolor: post.lifecycleState === 'PUBLISHED' ? '#0A66C2' : '#888',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                        {post.content?.media?.id?.includes(':image:') && (
                          <ImageIcon sx={{ color: '#0A66C2' }} />
                        )}
                        {post.content?.media?.id?.includes(':video:') && (
                          <VideoIcon sx={{ color: '#886CC0' }} />
                        )}
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
                        {new Date(post.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.4,
                        }}
                      >
                        {post.commentary?.substring(0, 100) || 'No caption'}
                        {post.commentary?.length > 100 && '...'}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default LinkedInAnalytics;
